import {
  OrderType,
  QuestionGroupStatus,
  QuestionGroupType,
  QuestionOwnerType,
  QuestionStatus,
  type GetQBankStructureLevelItemsByQBankStructureIdQuery,
  type GetQBankStructureLevelsByQBankStructureIdQuery,
  type SearchQuestionsForQuestionReviewTaskQuery,
  type SearchQuestionGroupsQuery,
} from '@exam-management/graphql';

import {
  EMPTY_LOCATION,
  LOCATION_LEVEL_ORDER,
  type QuestionType,
} from '../../constants/question-selection';
import type { LocationSchemaFormInput } from '../../providers';
import type { Question } from '../add-question-list/columns';

export interface QuestionSearchParams {
  courseId: string;
  levelItemId: string;
  currentPage: number;
  pageSize: number;
}

export interface BuildQuestionRowsParams {
  data?: SearchQuestionsForQuestionReviewTaskQuery;
  internalUserEmailDomains: string[];
}

export interface BuildQuestionGroupRowsParams {
  data?: SearchQuestionGroupsQuery;
  internalUserEmailDomains: string[];
}

export interface AddQuestionModalFilterState {
  questionType: QuestionType;
  location?: LocationSchemaFormInput;
  defaultLevelItemId: string;
}

const EMPTY_EMAIL = '';

export const createQuestionSearchVariables = ({
  courseId,
  levelItemId,
  currentPage,
  pageSize,
}: Readonly<QuestionSearchParams>) => ({
  search: {
    refQBankStructureId: courseId,
    levelItemId,
    status: [QuestionStatus.PreDraft],
    questionGroupType: [QuestionGroupType.Single],
  },
  paging: {
    currentPage,
    pageSize,
  },
});

export const createQuestionGroupSearchVariables = ({
  courseId,
  levelItemId,
  currentPage,
  pageSize,
}: Readonly<QuestionSearchParams>) => ({
  search: {
    refQBankStructureId: courseId,
    levelItemId,
    questionGroupType: [QuestionGroupType.ItemSet],
    status: [QuestionGroupStatus.PreDraft],
  },
  paging: {
    currentPage,
    pageSize,
  },
  order: {
    field: 'createdAt',
    type: OrderType.Asc,
  },
});

export const getActiveLevelItemId = ({
  location = EMPTY_LOCATION,
  defaultLevelItemId,
}: Readonly<Pick<AddQuestionModalFilterState, 'location' | 'defaultLevelItemId'>>) => {
  for (const key of LOCATION_LEVEL_ORDER) {
    const value = location[key]?.value;
    if (value) {
      return value;
    }
  }

  return defaultLevelItemId;
};

export const createRequestKey = ({
  questionType,
  courseId,
  levelItemId,
}: Readonly<{
  questionType: QuestionType;
  courseId: string;
  levelItemId: string;
}>) => [questionType, courseId, levelItemId].join(':');

export const buildQuestionRows = ({
  data,
  internalUserEmailDomains,
}: Readonly<BuildQuestionRowsParams>): Question[] => {
  const questions = data?.qctSearchQuestions.questions ?? [];

  return questions.map((question) => ({
    id: question.id ?? '',
    code: question.refCode ?? '',
    createdBy: {
      name: question.metadata.owner?.value ?? '',
      email: getOwnerEmail(question.metadata.owner?.type, internalUserEmailDomains),
    },
    questionGroupType: QuestionGroupType.Single,
    questionIds: question.id ? [question.id] : [],
    updateAt: question.updatedAt,
  }));
};

export const buildQuestionGroupRows = ({
  data,
  internalUserEmailDomains,
}: Readonly<BuildQuestionGroupRowsParams>): Question[] => {
  const questionGroups = data?.qctSearchQuestionGroups?.questionGroups ?? [];

  return questionGroups
    .map((questionGroup) => {
      const questionIds =
        questionGroup.questions
          ?.map((question) => question?.id)
          .filter((questionId): questionId is string => Boolean(questionId)) ?? [];

      return {
        id: questionGroup.id ?? '',
        code: questionGroup.refCode ?? '',
        createdBy: {
          name:
            questionGroup.questions?.find((question) => question?.metadata.owner?.value)?.metadata
              .owner?.value ?? '',
          email: getQuestionGroupOwnerEmail(questionGroup, internalUserEmailDomains),
        },
        updateAt: questionGroup.updatedAt,
        questionGroupType: QuestionGroupType.ItemSet,
        subQuestionCount: questionGroup.questions?.length,
        itemSetName: questionGroup.name,
        questionIds,
      };
    })
    .filter((questionGroup) => questionGroup.questionIds.length > 0);
};

export const getModalLevelOptions = (
  qbsLevels: GetQBankStructureLevelsByQBankStructureIdQuery,
  qbsLevelItems: GetQBankStructureLevelItemsByQBankStructureIdQuery,
) => {
  const levels = [...(qbsLevels.qbsGetQBankStructureLevelsByQBankStructureId ?? [])]
    .sort((left, right) => left.level - right.level)
    .filter((level) => level.level !== 1);

  const items = (qbsLevelItems.qbsGetQBankStructureLevelItemsByQBankStructureId ?? [])
    .map((item) => {
      const qBankStructureLevel = levels.find((level) => level.id === item.qBankStructureLevelId);

      if (!qBankStructureLevel) {
        return null;
      }

      return {
        ...item,
        qBankStructureLevel,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    qBankStructureLevels: levels,
    qBankStructureLevelItemsData: items,
  };
};

const getOwnerEmail = (
  ownerType: QuestionOwnerType | null | undefined,
  internalUserEmailDomains: string[],
) => {
  if (ownerType !== QuestionOwnerType.Internal) {
    return EMPTY_EMAIL;
  }

  return internalUserEmailDomains[0] ? `a@${internalUserEmailDomains[0]}` : EMPTY_EMAIL;
};

type QuestionGroupRow = NonNullable<
  SearchQuestionGroupsQuery['qctSearchQuestionGroups']
>['questionGroups'][number];

const getQuestionGroupOwnerEmail = (
  questionGroup: QuestionGroupRow,
  internalUserEmailDomains: string[],
) => {
  const hasInternalOwner = questionGroup.questions?.some(
    (question) => question?.metadata.owner?.type === QuestionOwnerType.Internal,
  );

  if (!hasInternalOwner) {
    return EMPTY_EMAIL;
  }

  return internalUserEmailDomains[0] ? `a@${internalUserEmailDomains[0]}` : EMPTY_EMAIL;
};
